import { useEffect } from 'react'
import { useQueryClient } from 'react-query'
import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { useAuth } from 'hooks/UseAuth'
import { optimisticUpdateFileInInfiniteLists, updateFileDetailCache } from 'hooks/queries/files/cacheUtils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import { DesignPreviewUpdate } from '../useDesignPreviewSync'
import { useRootStore } from 'stores/RootStore'

interface UseDesignPreviewBroadcastOptions {
  designKey: string | undefined
  enabled: boolean
}

/**
 * Subscribes to DesignPreviewChannel broadcasts for a single design.
 * When a preview_update message is received, updates the file caches
 * to show the new image and clear generating state.
 */
export const useDesignPreviewBroadcast = ({ designKey, enabled }: UseDesignPreviewBroadcastOptions) => {
  const auth = useAuth()
  const websocketConnection = useWebsocketConnection()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const { generationTimestampStore } = useRootStore()

  useEffect(() => {
    if (!enabled || !designKey || !auth || !websocketConnection) return

    const subscription = websocketConnection.subscriptions.create(
      {
        channel: 'DesignPreviewChannel',
        key: designKey,
      },
      {
        received(data: DesignPreviewUpdate) {
          if (data.op === 'preview_update') {
            optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, designKey, (f) => ({
              ...f,
              image_url: data.image_url,
              image_url_generating: false,
              duration: data.duration || f.duration,
              is_generating: false,
            }))

            updateFileDetailCache<AlmanacFileShowItem>(queryClient, workspaceId, designKey, (file) => ({
              ...file,
              image_url: data.image_url,
              image_url_generating: false,
              duration: data.duration || file.duration,
              is_generating: false,
            }))

            queryClient.invalidateQueries({
              queryKey: filesKeys.detail(workspaceId, designKey),
            })

            generationTimestampStore.completeGeneration(designKey)
          }
        },
      },
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [auth, websocketConnection, designKey, enabled, queryClient, workspaceId, generationTimestampStore])
}
